import {Localize} from "@dp-instant/i18n";
import cn from "classnames";
import {useEffect, useRef, useState} from "preact/hooks";
import {apiClient} from "@game/api/ApiClient";
import {playUIClickSound} from "@game/helpers";
import {hapticMedium} from "@game/haptic";
import {SVG} from "@game/ui/images";
import "./RewardedProgressBar.less";

interface Milestone {
    views: number;
    reward: number;
    type: "soft" | "hard";
    claimed: boolean;
}

interface ClaimedReward {
    milestone: number;
    reward: number;
    type: string;
}

interface AdResult {
    views: number;
    milestones: Milestone[];
    claimedRewards: ClaimedReward[];
}

interface ProgressState {
    views: number;
    milestones: Milestone[];
    loading: boolean;
    watching: boolean;
}

interface RewardedProgressBarProps {
    isPremium: boolean;
    onBalanceChange?: (soft: number, hard: number) => void;
}

export const RewardedProgressBar = ({ isPremium, onBalanceChange }: RewardedProgressBarProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [state, setState] = useState<ProgressState>({
        views: 0,
        milestones: [],
        loading: true,
        watching: false,
    });

    useEffect(() => {
        apiClient.getDailyAdProgress()
            .then((data: { views: number; milestones: Milestone[] }) => {
                setState((s) => ({ ...s, views: data.views, milestones: data.milestones, loading: false }));
            })
            .catch(() => {
                setState((s) => ({ ...s, loading: false }));
            });
    }, []);

    // Scroll to current progress on load and after state changes
    useEffect(() => {
        if (!scrollRef.current || state.milestones.length === 0) return;
        const container = scrollRef.current;
        const maxViews = state.milestones[state.milestones.length - 1]?.views ?? 20;
        const innerWidth = container.scrollWidth;
        const viewportWidth = container.clientWidth;
        const progressRatio = Math.min(1, state.views / maxViews);
        const targetScroll = (progressRatio * innerWidth) - (viewportWidth / 2);
        container.scrollLeft = Math.max(0, targetScroll);
    }, [state.views, state.milestones, state.loading]);

    const applyRewards = (result: AdResult) => {
        let softTotal = 0;
        let hardTotal = 0;
        for (const r of result.claimedRewards) {
            if (r.type === "soft") softTotal += r.reward;
            else hardTotal += r.reward;
        }
        if (softTotal > 0 || hardTotal > 0) {
            onBalanceChange?.(softTotal, hardTotal);
        }
    };

    const watchAd = async () => {
        if (state.watching) return;

        playUIClickSound();
        hapticMedium();
        setState((s) => ({ ...s, watching: true }));

        try {
            if (isPremium) {
                const result = await apiClient.premiumClaimAd();
                if (result) {
                    applyRewards(result);
                    setState((s) => ({
                        ...s,
                        views: result.views,
                        milestones: result.milestones,
                        watching: false,
                    }));
                    return;
                }
            } else {
                const { adManager } = await import("@game/ads/AdManager");
                let adShown = false;
                try { adShown = await adManager.showRewarded(); } catch {}

                if (!adShown) {
                    setState((s) => ({ ...s, watching: false }));
                    return;
                }

                const result = await apiClient.reportRewardedComplete();
                if (result) {
                    applyRewards(result);
                    setState((s) => ({
                        ...s,
                        views: result.views,
                        milestones: result.milestones,
                        watching: false,
                    }));
                    return;
                }
            }
        } catch {}

        setState((s) => ({ ...s, watching: false }));
    };

    if (state.loading || state.milestones.length === 0) return null;

    const maxViews = state.milestones[state.milestones.length - 1]?.views ?? 20;
    const progressPct = Math.min(100, (state.views / maxViews) * 100);
    const allClaimed = state.milestones.every((m) => m.claimed);

    const TRACK_WIDTH_PX = 600;

    return (
        <div className="rewarded-progress">
            <div className="rewarded-progress__header">
                <span className="rewarded-progress__title">
                    {Localize.get("ui-shop-daily-rewards", "DAILY REWARDS")}
                </span>
                <span className="rewarded-progress__count">{state.views}/{maxViews}</span>
            </div>

            <div className="rewarded-progress__scroll" ref={scrollRef}>
                <div className="rewarded-progress__track" style={{ width: `${TRACK_WIDTH_PX}px` }}>
                    <div className="rewarded-progress__bar">
                        <div className="rewarded-progress__bar-fill" style={{ width: `${progressPct}%` }} />
                    </div>
                    {state.milestones.map((m, i) => {
                        const pos = (m.views / maxViews) * 100;
                        return (
                            <div
                                key={i}
                                className={cn("rewarded-progress__ms", {
                                    "rewarded-progress__ms--reached": state.views >= m.views,
                                    "rewarded-progress__ms--claimed": m.claimed,
                                })}
                                style={{ left: `${pos}%` }}
                            >
                                <div className="rewarded-progress__ms-dot">
                                    {m.claimed
                                        ? "✓"
                                        : m.type === "hard"
                                            ? "⭐"
                                            : <span className="rewarded-progress__ms-coin-icon"><SVG.COINS /></span>
                                    }
                                </div>
                                <div className="rewarded-progress__ms-label">
                                    {m.reward}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {!allClaimed && (
                <button
                    className={cn("rewarded-progress__btn", {
                        "rewarded-progress__btn--loading": state.watching,
                    })}
                    onClick={watchAd}
                    disabled={state.watching}
                >
                    {state.watching
                        ? "..."
                        : isPremium
                            ? "🎁 " + Localize.get("ui-shop-claim", "CLAIM")
                            : "▶ " + Localize.get("ui-shop-watch-ad", "WATCH")}
                </button>
            )}
        </div>
    );
};
